/*
 * 戒导航 · 通知管理（jiedh/notification）前台初始化
 * =============================================================================
 * 职责：给「通知面板」里**每一条通知**加一个行内「删除」按钮。
 *
 * 背景（核心现状，已逐行核对）：
 *   · 面板顶部只有两个图标：全部标记已读、全部清空
 *     （vendor/flarum/core/js/src/forum/components/NotificationList.js 的 controlItems()）；
 *   · 单条通知组件基类 Notification 的 actionItems() 里**只有**一个「标记为已读」按钮
 *     （vendor/flarum/core/js/src/forum/components/Notification.tsx），
 *     核心既不提供「删除单条」的界面，也不提供 `DELETE /api/notifications/{id}` 接口
 *     ⇒ 用户能做的只有「全部清空」，无法只删掉某一条，这正是「面板无法删除」的根因。
 *
 * 为什么扩展点是 Notification.prototype.actionItems（一处覆盖全站）：
 *   actionItems() 定义在**抽象基类** Notification 上，所有通知子类
 *   （回复 / 点赞 / 关注 / 提及 / 本项目的处置回执 …）都继承它，且 view() 里固定调用
 *   `actions={this.actionItems().toArray()}` 交给 HeaderListItem 渲染。
 *   因此在这里 extend 一次，**所有通知类型**都会自动获得删除按钮，
 *   无需逐个类型重复注册（也无需关心未来新增的通知类型）。
 *
 * 为什么 extend 的优先级用 -1000（最后执行）：
 *   extend() 是「包裹」语义：它在当前方法外面再包一层。
 *   数值越大越先执行（与 ItemList 一致），用 -1000 保证我们包在**最外层**，
 *   即使其它扩展也用 extend/override 动过 actionItems，我们的按钮也不会被冲掉。
 *   （packages/content-guard 出于同样的理由用了 -1000。）
 *
 * 数据流（与后端 DeleteNotificationController 一一对应）：
 *   点击 🗑 → 二次确认 → DELETE /api/notifications/{id}
 *            → 成功后：本地从 app.notifications 移除该行 + 未读角标 -1
 *            → 失败时：弹错误提示，界面不动（不做乐观更新，因此无需回滚）
 *
 * ⚠️ 文件名必须是 .tsx（不是 .ts）：
 *   本文件含 JSX，babel 对 .ts 按「纯 TypeScript」解析（JSX 标签会被当成类型参数报
 *   `Unexpected token, expected ","`），只有 .tsx 才开启 TSX 解析。
 *   flarum-webpack-config 的 resolve.extensions 已包含 .tsx，因此
 *   js/forum.ts 里的 `export * from './src/forum';` 无需改动即可命中本文件。
 *
 * ⚠️ JSX 写法约定（勿改成 import mithril）：
 *   本项目所有扩展都不 import mithril，JSX 由 flarum-webpack-config 的 babel
 *   以 `pragma: 'm'` 编译成对**全局标识符 `m`** 的调用；m.redraw() 同理直接可用
 *   （详见 packages/icon-picker / packages/review 的同类写法）。
 */
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Notification from 'flarum/forum/components/Notification';
import extractText from 'flarum/common/utils/extractText';
/*
 * 翻译键（对应 resources/locale/zh-Hans.yml、en.yml）。
 * 单独抽成常量，避免多处硬编码字符串漂移。
 */
const TRANS_DELETE_TOOLTIP = 'jiedh-notification.forum.delete_tooltip';
const TRANS_DELETE_CONFIRM = 'jiedh-notification.forum.delete_confirm';
const TRANS_DELETE_ERROR = 'jiedh-notification.forum.delete_error';

/**
 * 删除一条通知：二次确认 → 请求后端 → 成功后本地移除该行并修正角标。
 *
 * @param notification 被点击的那条通知模型（来自 Notification 组件的 attrs）
 */
function deleteNotification(notification) {
  /*
   * 二次确认：删除不可撤销，必须防误触。
   * 文案风格与核心「清空全部通知」的 delete_all_confirm 保持一致；
   * app.translator.trans() 返回 Mithril 节点，交给原生 confirm() 前必须用
   * extractText() 转成纯文本（与核心 NotificationsDropdown 的做法相同）。
   */
  if (!confirm(extractText(app.translator.trans(TRANS_DELETE_CONFIRM)))) {
    return;
  }
  const wasUnread = !notification.isRead();
  const url = app.forum.attribute('apiUrl') + '/notifications/' + notification.id();

  /*
   * 刻意**不做乐观更新**：先等服务端返回 204，再动本地状态。
   * 理由：这样就不存在「请求失败还要把行塞回去」的回滚逻辑，行为最确定；
   *      代价只是本地接口的几十毫秒延迟，用户几乎无感。
   * （核心的「清空全部」是乐观更新，因为它清空的是整份列表，失败后重开面板即可恢复。）
   */
  app.request({
    url,
    method: 'DELETE'
  }).then(() => {
    /*
     * 从当前分页里移除这一行。
     * app.notifications 是 PaginatedListState 的实例，其 remove(model)
     * 正是为「删除单条后局部更新」准备的公开方法（与 clear() / push() 同级），
     * 因此不需要自造列表状态，也不会触发整页重新请求。
     */
    app.notifications.remove(notification);

    /*
     * 角标修正：删掉的若是**未读**通知，未读数要同步 -1。
     * 写法照抄核心 Notification.markAsRead()，用 Math.max(0, …) 兜底，
     * 避免网络竞态导致出现负数角标。
     * （newNotificationCount 由 NotificationListState.load() 在展开面板时清零，此处无需处理。）
     */
    if (wasUnread) {
      app.session.user?.pushAttributes({
        unreadNotificationCount: Math.max(0, (app.session.user.unreadNotificationCount() ?? 0) - 1)
      });
    }
    m.redraw();
  }).catch(() => {
    // 失败（401/404/网络错误等）：界面保持原样，只给出明确反馈，用户可以重试。
    app.alerts.show({
      type: 'error'
    }, app.translator.trans(TRANS_DELETE_ERROR));
    m.redraw();
  });
}

/**
 * 往「通知行内操作区」追加删除按钮。
 *
 * 该回调会被注册到 Notification 基类原型上，对所有通知类型生效。
 */
function addDeleteAction(items) {
  const notification = this.attrs.notification;
  items.add('jiedhDelete', m(Button
  /*
   * 类名三件套 + 本包私有类 jiedhNotification-delete：
   *   · Notification-action / Button / Button--link → 与核心「标记为已读」按钮完全同款，
   *     自动继承通知行内操作区的排布与配色；
   *   · jiedhNotification-delete → 本包样式的唯一挂钩点（见 less/forum.less）。
   *
   * ⚠️ 为什么必须挂私有类，而不能只靠核心的 CompactActions：
   *   核心把行内按钮统一设为 opacity: 0，只在 行 hover / focus 时才显现
   *   （vendor/flarum/core/less/forum/HeaderList.less）。
   *   这对「标记为已读」合适，但「删除通知」如果也藏起来：
   *     1) 桌面端用户察觉不到入口（实测反馈就是「通知面板无法删除」）；
   *     2) 移动端没有 hover，按钮永远不会出现 —— 功能等于不存在；
   *   所以本包用更高特异性的选择器单独把**这个按钮**点亮，核心按钮行为维持原样。
   */, {
    className: "Notification-action Button Button--link jiedhNotification-delete",
    icon: "fas fa-trash-alt"
    // title + aria-label 双写：鼠标悬停有原生提示，读屏软件也能正确朗读（与核心一致）。
    ,
    title: app.translator.trans(TRANS_DELETE_TOOLTIP),
    "aria-label": app.translator.trans(TRANS_DELETE_TOOLTIP),
    onclick: e => {
      /*
       * ⚠️ 这两行必须保留，缺一不可：
       * 通知整行本身是一个指向帖子的链接（HeaderListItem 用 <Link> 渲染），
       * 不 preventDefault / stopPropagation 的话，点「删除」会顺带跳转到帖子页面。
       * 核心的 markAsRead 按钮同样做了这两步。
       */
      e.preventDefault();
      e.stopPropagation();
      deleteNotification(notification);
    }
  }),
  /*
   * 优先级 50 < 核心「标记为已读」的 100
   * ⇒ ItemList 按数值从大到小排列，删除按钮会排在「已读」按钮之后（最右侧）。
   */
  50);
}

// 初始化器 name 必须与扩展 ID 对应（本包 ID 为 jiedh/notification → CLI 形式 jiedh-notification），
// 它同时用于 Flarum 的错误归因（app.currentInitializerExtension）。
// 第三参数 -1000 的用途见文件头「为什么 extend 的优先级用 -1000」。
app.initializers.add('jiedh-notification', () => {
  /*
   * 这里按 Flarum 官方惯例把 extend 断言成 any：
   * Notification 是抽象类，其 prototype 上的方法在 TS 里不易精确推断，
   * 官方扩展（如 flarum/markdown）在同类场景也这样写，不影响运行时行为。
   */
  extend(Notification.prototype, 'actionItems', addDeleteAction);
}, -1000);